// @ts-strict-ignore
import React, {
  useContext,
  useMemo,
} from 'react';

import {
  selectCustomLists, selectWorkflowData, selectWorkflowProps,
} from 'flow/store/redux/selectors';
import {
  getAllMLAsWithFootnoteStringTokens,
  getLabelRequirementsForFootnoteSentence,
  getMLAFilterComponentLabel,
  getPresentationalValue,
  isRuleWithMLAAThreshold,
} from 'flow/service/mla';
import { useAppSelector } from '~/store/reduxHooks';

import {
  FootnoteItem, FootnoteSentencesContainer, FootnoteTokenContainer, StyledSuperset,
} from '../styles';
import { RuleEditorContext } from '../../NodeEditorDrawer/context';
import type { RulesWithMLA } from '../types';

function MLAFootnotesContainer({ rulesWithMLA = null }: { rulesWithMLA?: RulesWithMLA }) {
  const {
    mlaUsedInRules,
    containerType,
  } = useContext(RuleEditorContext);

  const { actions } = useAppSelector(selectWorkflowData);
  const { services } = useAppSelector(selectWorkflowProps);
  const customLists = useAppSelector(selectCustomLists);
  const rulesWithMLAData = rulesWithMLA || mlaUsedInRules;

  const allMLAsWithFootnoteStringTokens = useMemo(() => getAllMLAsWithFootnoteStringTokens({
    actions,
    rulesWithMLA: rulesWithMLAData,
    services,
    customLists,
  }), [actions, rulesWithMLAData, services, customLists]);

  return rulesWithMLAData?.length ? (
    <FootnoteSentencesContainer
      mode={isRuleWithMLAAThreshold(rulesWithMLAData[0]) ? 'rule' : 'expression'}
      containerType={containerType}
      newEditor={!!rulesWithMLA}
      data-testid="mlaFootnotesContainer"
    >
      {allMLAsWithFootnoteStringTokens?.map((mla, mlaIdx) => (
        <FootnoteItem key={`${mla.publicId}-${mla.type}`}>
          <StyledSuperset>{mlaIdx + 1}</StyledSuperset>
          {mla.presentationalTokens.map(token => {
            const labelRequirements = getLabelRequirementsForFootnoteSentence({
              token,
              attributeName: mla.attributeName,
            });
            return (
              (token.isOptional && token.value) || !token.isOptional
                ? (
                  <FootnoteTokenContainer key={token.placeholder}>
                    <span key={token.placeholder}>
                      {token.component ? (
                        getPresentationalValue({
                          configuredValue: token.value,
                          component: token.component,
                          attributeName: mla.attributeName,
                          labelRequirements,
                        }) || `{${getMLAFilterComponentLabel({
                          componentData: token.component,
                        })}}`
                      ) : token.label}
                    </span>
                  </FootnoteTokenContainer>
                ) : null);
          })}
        </FootnoteItem>
      ))}
    </FootnoteSentencesContainer>
  ) : null;
}

export default MLAFootnotesContainer;
